import type { DonationPaymentMethod } from './donation.js';
import { FORM_FILE_MAX_BYTES } from './form-file.js';
import type { Money } from './money.js';
import { addDays } from './rate-day.js';
import { referenceCodesNamed } from './reconciliation-match.js';
import { normalizeReferenceCode } from './reference-code.js';
import type { AccountTransactionSource } from './statement-profile.js';

/**
 * How money typed by hand arrived — docs/decisions/0100. A transfer is typed
 * from its slip (*dekont*), a cheque from the cheque, and cash from what was
 * counted at a desk or out of a donation box.
 */
export const MANUAL_PAYMENT_METHODS = ['bank_transfer', 'cheque', 'cash'] as const;
export type ManualPaymentMethod = (typeof MANUAL_PAYMENT_METHODS)[number];

/** The two sources a person types; every other line arrives from a statement or a provider. */
export const MANUAL_SOURCES = [
  'manual_cash',
  'manual_bank',
] as const satisfies readonly AccountTransactionSource[];
export type ManualSource = (typeof MANUAL_SOURCES)[number];

export function isManualSource(source: string): source is ManualSource {
  return (MANUAL_SOURCES as readonly string[]).includes(source);
}

/**
 * The line a method writes. A transfer and a cheque are bank money — the
 * cheque on the bank account it will be paid into — and cash is the cash box's.
 */
export const MANUAL_PAYMENT_SOURCE: Readonly<Record<ManualPaymentMethod, ManualSource>> = {
  bank_transfer: 'manual_bank',
  cheque: 'manual_bank',
  cash: 'manual_cash',
};

/** The kind of account a method's money is kept in, which is the only kind its picker offers. */
export const MANUAL_PAYMENT_ACCOUNT_KIND: Readonly<Record<ManualPaymentMethod, 'bank' | 'cash'>> = {
  bank_transfer: 'bank',
  cheque: 'bank',
  cash: 'cash',
};

/**
 * What a donation recorded `unspecified` learns when a typed payment is
 * approved. There is no cheque payment method, so a cheque is `other` — the
 * method outside the list — rather than claiming a transfer nobody made.
 */
export const MANUAL_PAYMENT_PROVES: Readonly<Record<ManualPaymentMethod, DonationPaymentMethod>> = {
  bank_transfer: 'bank_transfer',
  cheque: 'other',
  cash: 'cash',
};

/**
 * Only a transfer must carry its slip: the slip is the one thing that says a
 * transfer was made. A cheque and cash are in somebody's hand, and a photograph
 * of them proves nothing the second person cannot ask about.
 */
export function manualPaymentSlipRequired(method: ManualPaymentMethod): boolean {
  return method === 'bank_transfer';
}

/** A slip is a photograph or a PDF, read under the same cap a form's file is. */
export const PAYMENT_SLIP_MAX_BYTES = FORM_FILE_MAX_BYTES;

/**
 * The methods whose money arrives through a provider and is settled by its
 * verified capture — never by somebody typing it.
 */
export const PROVIDER_SETTLED_PAYMENT_METHODS: readonly DonationPaymentMethod[] = [
  'card',
  'pos',
  'paypal',
];

export const MANUAL_PAYMENT_INELIGIBILITIES = ['provider_method', 'box_not_cash'] as const;
export type ManualPaymentIneligibility = (typeof MANUAL_PAYMENT_INELIGIBILITIES)[number];

/**
 * Why a donation cannot be paid by hand with this method, or null. A card,
 * POS or PayPal donation is settled by its provider. A donation box's donation is
 * the cash counted out of the box, so it is paid by hand in cash and nothing
 * else.
 */
export function manualPaymentIneligibility(
  donation: { paymentMethod: string; fromDonationBox: boolean },
  method: ManualPaymentMethod,
): ManualPaymentIneligibility | null {
  if ((PROVIDER_SETTLED_PAYMENT_METHODS as readonly string[]).includes(donation.paymentMethod)) {
    return 'provider_method';
  }
  if (donation.fromDonationBox && method !== 'cash') return 'box_not_cash';
  return null;
}

/** The method a typed line was recorded with, read back from its raw row; null for anything else. */
export function manualPaymentMethodOf(rawRow: unknown): ManualPaymentMethod | null {
  if (rawRow === null || typeof rawRow !== 'object') return null;
  const method = (rawRow as { method?: unknown }).method;
  return typeof method === 'string' &&
    (MANUAL_PAYMENT_METHODS as readonly string[]).includes(method)
    ? (method as ManualPaymentMethod)
    : null;
}

/** Who typed a line by hand, read back from its raw row; null for a line nobody typed. */
export function manualLineTypistOf(rawRow: unknown): string | null {
  if (rawRow === null || typeof rawRow !== 'object') return null;
  const typist = (rawRow as { enteredByUserId?: unknown }).enteredByUserId;
  return typeof typist === 'string' && typist.length > 0 ? typist : null;
}

/**
 * The method a typed line's money arrived by: what it was recorded with, or —
 * a cash line typed before the method was kept — what its source admits.
 */
export function manualLineMethodOf(source: ManualSource, rawRow: unknown): ManualPaymentMethod {
  return manualPaymentMethodOf(rawRow) ?? (source === 'manual_cash' ? 'cash' : 'bank_transfer');
}

/**
 * Where a payment typed by hand stands, read from its line and matches —
 * docs/decisions/0100: a second person has not approved it yet; it is approved
 * and its transfer or cheque has not been found on the bank's statement; it has
 * been found there; it is approved cash, which no statement line ever brings; or
 * it is void — rejected, withdrawn, or held by nothing any more.
 */
export const MANUAL_PAYMENT_STAGES = [
  'awaiting_approval',
  'awaiting_bank',
  'cleared',
  'approved',
  'void',
] as const;
export type ManualPaymentStage = (typeof MANUAL_PAYMENT_STAGES)[number];

/**
 * How many days an approved transfer or cheque may wait for its statement line
 * before the awaiting-bank list flags it — the orchestrator's override recorded
 * in docs/decisions/0100. A transfer is booked within days; a cheque that has
 * not reached the bank in two weeks is somebody's question.
 */
export const MANUAL_PAYMENT_AWAITING_BANK_DAYS = 14;

/** The stage a payment's line and matches put it in. */
export function manualPaymentStage(payment: {
  readonly source: ManualSource;
  readonly excluded: boolean;
  /** A match of the payment still waits for a second person. */
  readonly waiting: boolean;
  /** A match of the payment is confirmed. */
  readonly approved: boolean;
  readonly amountMinor: bigint;
  readonly clearedMinor: bigint;
}): ManualPaymentStage {
  if (payment.excluded) return 'void';
  if (payment.waiting) return 'awaiting_approval';
  if (payment.clearedMinor > 0n && payment.clearedMinor >= payment.amountMinor) return 'cleared';
  if (!payment.approved) return 'void';
  return payment.source === 'manual_bank' ? 'awaiting_bank' : 'approved';
}

/** Whole days from the day the money changed hands to `today`, never below zero. */
export function manualPaymentAgeDays(receivedOn: string, today: string): number {
  return Math.max(0, daysFrom(receivedOn, today));
}

/**
 * How far either side of the day a slip names a statement line already in the
 * system counts as perhaps the same money: a transfer is booked by the bank a
 * few days after it was sent, and a slip is often typed late.
 */
export const MANUAL_PAYMENT_LINE_WINDOW_DAYS = { before: 3, after: 14 } as const;

/**
 * Why a payment typed from a slip or a cheque is offered as the one a
 * statement line brings — docs/decisions/0100: the line's text names one of
 * the payment's donation codes or its slip number, or it is exactly the
 * payment's amount, in the days a transfer takes to be booked.
 */
export const CLEARING_READINGS = ['reference', 'amount_date'] as const;
export type ClearingReading = (typeof CLEARING_READINGS)[number];

/** The statement line a clearing would hold, as the matcher reads it. */
export type ClearingBankLine = {
  readonly amount: Money;
  readonly allocatedMinor: bigint;
  readonly bookedOn: string;
  readonly description: string | null;
  readonly counterpartyName: string | null;
};

/**
 * An approved `manual_bank` payment on the line's own account that no
 * statement line has been found for yet. `referenceCodes` are its donations'.
 */
export type ClearableManualPayment = {
  readonly id: string;
  readonly amount: Money;
  readonly bookedOn: string;
  readonly externalRef: string | null;
  readonly referenceCodes: readonly string[];
};

export type ClearingCandidate = {
  readonly manualTransactionId: string;
  readonly reading: ClearingReading;
  readonly score: number;
  /** The payment takes the whole of what is left of the line. */
  readonly exactAmount: boolean;
};

const CLEARING_REFERENCE_EXACT_SCORE = 100;
const CLEARING_REFERENCE_SCORE = 90;
const CLEARING_AMOUNT_DATE_SCORE = 60;
/** A slip number shorter than this is too likely to occur in any line's text by chance. */
const SLIP_NUMBER_MIN_LENGTH = 4;
const DAY_MS = 86_400_000;

/**
 * The days a payment's own date may fall on for a statement line booked on
 * `bookedOn` to be its money: the line is booked from three days before the
 * slip's day to fourteen after it — the same window `MANUAL_PAYMENT_LINE_EXISTS`
 * reads from the other side.
 */
export function clearingWindowFor(bookedOn: string): { from: string; to: string } {
  return {
    from: addDays(bookedOn, -MANUAL_PAYMENT_LINE_WINDOW_DAYS.after),
    to: addDays(bookedOn, MANUAL_PAYMENT_LINE_WINDOW_DAYS.before),
  };
}

/**
 * The days a statement line may be booked on to bring the money of a payment
 * received on `receivedOn` — the same window as `clearingWindowFor`, read from
 * the payment's side: three days before the slip's day to fourteen after it.
 */
export function bankLineWindowFor(receivedOn: string): { from: string; to: string } {
  return {
    from: addDays(receivedOn, -MANUAL_PAYMENT_LINE_WINDOW_DAYS.before),
    to: addDays(receivedOn, MANUAL_PAYMENT_LINE_WINDOW_DAYS.after),
  };
}

function compact(text: string): string {
  return text.toUpperCase().replace(/[^0-9A-Z]/g, '');
}

function daysFrom(fromDay: string, toDay: string): number {
  return Math.round(
    (Date.parse(`${toDay}T00:00:00Z`) - Date.parse(`${fromDay}T00:00:00Z`)) / DAY_MS,
  );
}

/**
 * The payments typed by hand a statement line may be the bank's side of,
 * strongest first — the matcher's pre-tier, asked before any donation is
 * proposed (docs/decisions/0100). A payment is offered only in the line's
 * currency, for no more than the line has left, and inside the booking
 * window. The line's text naming one of its donation codes or its slip number
 * is the strong reading — whatever the amount, since one transfer may carry
 * several payments; otherwise only a payment of exactly what is left is
 * offered. Clearing is confirmed by a person, one at a time, never in bulk.
 */
export function rankClearings(
  line: ClearingBankLine,
  payments: readonly ClearableManualPayment[],
): ClearingCandidate[] {
  const remainderMinor = line.amount.amountMinor - line.allocatedMinor;
  if (remainderMinor <= 0n) return [];

  const texts = [line.description, line.counterpartyName].filter(
    (text): text is string => text !== null && text !== '',
  );
  const named = new Set(referenceCodesNamed(line));
  const compactTexts = texts.map(compact);
  const window = clearingWindowFor(line.bookedOn);

  const ranked: (ClearingCandidate & { distance: number })[] = [];
  for (const payment of payments) {
    if (payment.amount.currency !== line.amount.currency) continue;
    if (payment.amount.amountMinor <= 0n || payment.amount.amountMinor > remainderMinor) continue;
    if (payment.bookedOn < window.from || payment.bookedOn > window.to) continue;

    const exactAmount = payment.amount.amountMinor === remainderMinor;
    const slip = payment.externalRef === null ? '' : compact(payment.externalRef);
    const namesSlip =
      slip.length >= SLIP_NUMBER_MIN_LENGTH && compactTexts.some((text) => text.includes(slip));
    const namesDonation = payment.referenceCodes.some((code) =>
      named.has(normalizeReferenceCode(code)),
    );
    const distance = Math.abs(daysFrom(payment.bookedOn, line.bookedOn));

    if (namesSlip || namesDonation) {
      ranked.push({
        manualTransactionId: payment.id,
        reading: 'reference',
        score: exactAmount ? CLEARING_REFERENCE_EXACT_SCORE : CLEARING_REFERENCE_SCORE,
        exactAmount,
        distance,
      });
    } else if (exactAmount) {
      ranked.push({
        manualTransactionId: payment.id,
        reading: 'amount_date',
        score: CLEARING_AMOUNT_DATE_SCORE,
        exactAmount,
        distance,
      });
    }
  }

  return ranked
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.distance - b.distance ||
        a.manualTransactionId.localeCompare(b.manualTransactionId),
    )
    .map(({ distance: _distance, ...candidate }) => candidate);
}
